// Usage:
//     let space;
//     ...
//     function setup() {
//         ...
//         space = new Space();
//         bigContainer.addChild(space);
//         ...
//     }
//     ...
//     function tick() {
//         ...
//         space.update(dt);
//         ...
//     }

import {Container, Rectangle} from "pixi.js";
import {Vector} from "./i.js"; // ./vector.js
import {Random} from "./i.js"; // ../random.js
import {logger} from "./i.js"; // ./logger.js
import {DEBUG, sq} from "./i.js"; // ./dump.js
import {CodeFactoryError} from "./i.js"; // ./code-factories.js
import {InvalidConfigError} from "./i.js"; // ./validators.js
import {Guesstimator} from "./i.js"; // ./guesstimator.js
import {Scanner, Grid} from "./i.js"; // ./scanner.js
import {Density} from "./i.js"; // ./density.js
import {Particles} from "./i.js"; // ./particles.js
import {Thing} from "./i.js"; // ./thing.js
import {Item} from "./i.js"; // ./drop.js
import {Player} from "./i.js"; // ./player.js
import {Cossette} from "./i.js"; // ./cossette.js
import {Prankster} from "./i.js"; // ./prankster.js
import {Meteorus} from "./i.js"; // ./meteorus.js
import {Station} from "./i.js"; // ./station.js
import {Interactor} from "./i.js"; // ./interactor.js

// Debug only
import {Compass} from "./i.js";

export class Space extends Container {
	// Create a new space manager
	// Only create one space manager per application; creating more results in
	// undefined behavior. game is a reference to the game that created this
	// space.
	constructor(game) {
		super();

		this.game = game;

		this.compassDirection = Random.angle();

		this._spawnQueue = [];
		this._nextThingId = 0;

		this._grid = new Grid();
		this.addChild(this._grid);

		this.scanner = new Scanner(this);

		// TODO: Possibly turn this into a map?
		this.things = [];

		this.particles = new Particles(this);
		this.addChild(this.particles.displayBack);

		// thingDisplays contain PIXI objects for displaying things on screen
		this.thingDisplays = new Container();
		this.thingDisplays.sortableChildren = true;
		this.addChild(this.thingDisplays);

		this.addChild(this.particles.displayFront);

		this.player = this.requestSpawn(Player, {});
		let originStation = this.requestSpawn(Station, {
			name: "Origin Station",
			pos: new Vector(0, 0)
		});
		// Add an arrow to the origin station
		// TODO: This timeout is a little hacky, but without it the arrow would be deleted
		// at the start of the first tick.
		setTimeout(() => {this.game.unicenter.gps.arrows.add(originStation.id)}, 100);
		this.requestSpawn(Station, {
			name: "Temporary Station",
			pos: new Vector(200, 0)
		});
		this.requestSpawn(Prankster, {
			time: 0
		});
		this.requestSpawn(Meteorus, {
			posGenerator: `motion/circle/0/0/100/${30 * 60}/${- Math.PI / 2}`,
			time: 0
		});
		this.requestSpawn(Meteorus, {
			posGenerator: `motion/circle/0/0/100/${30 * 60}/0`,
			time: 0
		});
		this.requestSpawn(Meteorus, {
			posGenerator: `motion/circle/0/0/100/${30 * 60}/${Math.PI / 2}`,
			time: 0
		});
		this.requestSpawn(Cossette, {
			sleepPosGenerator: `motion/circle/0/0/5/${4 * 60}/0`,
			time: 0
		});
		if (DEBUG) {
			// Add a Meteorus close to the station for testing purposes
			this.requestSpawn(Meteorus, {
				posGenerator: `motion/circle/0/0/9/${30 * 60}/${Math.PI}`,
				time: 0
			});
			for (let i = 0; i < 30; ++i) {
				this.requestSpawn(Compass, {pos: new Vector(3, 0).chooseNearby(7)});
			}
		}

		this.addChild(this.scanner);

		this._guesstimator = new Guesstimator(this);
		this.interactor = new Interactor(this);

		this.density = new Density();

		this.time = 0;

		// Whether the player can currently dock
		// Used for showing/hiding various hints and buttons.
		this.canDock = false;

		// Add event listeners
		this.interactive = true;
		this.hitArea = new Rectangle(0, 0, 1, 1);
		this.on("pointerdown", this._startFiring);
		this.on("pointerup", this._stopFiring);
		this.on("pointerupoutside", this._stopFiring);
	}

	// Advance all things in space by one frame
	// dt is the time since last update, in seconds. screenSize is a vector
	// containing the screen size, in pixels.
	update(dt, screenSize) {
		// Increment time at the start of the method because this method runs
		// after dt has already passed.
		this.time += dt;

		let pixelsPerFlare = Math.sqrt((screenSize.x * screenSize.y) / (8 * 6));
		// Hack: update hit area
		this.hitArea.width = screenSize.x;
		this.hitArea.height = screenSize.y;

		this._handleSpawnRequests();
		this._guesstimate(dt, screenSize, pixelsPerFlare);
		this._generateTacticalReport();
		this._move(dt);
		this._collide(dt);
		this._kill();
		this._redraw(screenSize, pixelsPerFlare);

		this.particles.update(dt, screenSize, pixelsPerFlare);
	}

	// Request a conversion from pos to position
	// pos is the pos to be converted; pixelsPerFlare is a real number. This
	// function is only guaranteed to work if the player hasn't moved since the
	// player was last redrawn.
	requestPosToPosition(pos, pixelsPerFlare) {
		return Vector.diff(pos, this.player.pos).scale(pixelsPerFlare)
			.add(this.player.display.position);
	}

	// Request to spawn a thing
	// thingClass is a reference to a class, and config is a config object.
	// config is enriched and then used to construct an instance of thingClass.
	// The resulting thing is added to space at the start of the next tick.
	// This function adds new properties to config. This function also returns a
	// reference to the newly spawned thing. Keep in mind that the reference is
	// returned immediately, but the thing is still only added to space on the
	// next tick.
	requestSpawn(thingClass, config) {
		// Save a copy of the provided config for logging purposes
		let configToLog = JSON.stringify(config);

		// Enrich config
		config.id = this._nextThingId++;
		config.space = this;

		// Spawn the thing
		try {
			let thing = new thingClass(config);
			this._spawnQueue.push(thing);
			return thing;
		} catch(e) {
			if ((e instanceof InvalidConfigError)
							|| (e instanceof CodeFactoryError)) {
				logger.error(
					"space.js",
					// TODO: make this log message minimization-friendly
					`invalid config for spawning ${thingClass.name}:`
									+ ` ${configToLog} (reason: ${e.message})`
				);
				return {};
			} else {
				throw e;
			}
		}
	}

	// Request a tactical report
	// filter is a function used to filter down the tactical report; it takes in
	// a report entry, and outputs true if this entry should be included and
	// false if this entry shouldn't be included. cacheName is a string under
	// which the request may be cached; requests with the same cacheName must
	// have the same filter.
	requestTacticalReport(filter, cacheName) {
		if (!this._tacticalReportCache.has(cacheName)) {
			this._tacticalReportCache.set(
				cacheName,
				this._tacticalReportFull.filter(filter)
			);
		}
		return this._tacticalReportCache.get(cacheName);
	}

	// Request a reference to the thing with the specific id
	// id is an integer. If the thing with the provided id is not found, the
	// function returns null.
	requestThingById(id) {
		let thing = this.things.find(t => (t.id === id));
		if (thing === undefined) return null;
		return thing;
	}

	// Request to drop drops
	// pos/radius/vel refer to the parent thing. dropList is an instance of
	// DropList.
	requestDrop(pos, radius, vel, dropList) {
		for (let dropName of dropList.getDrops()) {
			this.requestSpawn(Item, {
				itemName: dropName,
				parentPos: pos,
				parentRadius: radius,
				parentVel: vel
			});
		}
	}

	// Save the state of space into an object
	save() {
		let state = {
			time: this.time,
			timeSinceSpawn: this.timeSinceSpawn,
			nextThingId: this._nextThingId
			// TODO: Possibly save this._spawnQueue in some way?
		};

		state.things = [];
		for (let thing of this.things) {
			let {config, isComplete} = thing.save();

			if (!isComplete) {
				logger.error(
					"space.js",
					// TODO: make this log message minimization-friendly
					`cannot save ${thing.constructor.name} (id ${thing.id})`
									+ ` because the following config is`
									+ ` incomplete: ${JSON.stringify(config)}`
				);
				continue;
			}

			state.things.push({
				// TODO: Use a code factory here
				thingClass: thing.constructor.name,
				config: config
			});
		}

		return state;
	}

	// Spawn requested things and clear the spawn queue
	_handleSpawnRequests() {
		if (this._spawnQueue.length > 0) {
			this.things.push(...this._spawnQueue)
			this.thingDisplays.addChild(
				...this._spawnQueue.map(thing => thing.display)
			);
			for (let spawn of this._spawnQueue) {
				this.interactor.addHitbox(spawn.hitbox);
			}
			this._spawnQueue = [];
		}
	}

	// Spawn and despawn enemies according to the guesstimation
	// dt is the time since last update, in seconds.
	_guesstimate(dt, screenSize, pixelsPerFlare) {
		this._guesstimator.updateBubble(Vector.scale(screenSize, 1 / pixelsPerFlare));

		// Despawn things
		for (let i = this.things.length; i--;) {
			let victim = this.things[i];
			if (!victim.CAN_DESPAWN) continue;
			if (victim.time_outside_bubble === null
							|| victim.DESPAWN_SAFETY_WINDOW === null) {
				logger.error(
					"space.js",
					"victim doesn't have required parameters for despawning"
				);
				continue;
			}
			if (this._guesstimator.isOutsideBubble(victim.pos)) {
				victim.timeOutsideBubble += dt;
			} else {
				victim.timeOutsideBubble = 0;
			}
			// Using <= to always continue when timeOutsideBubble === 0
			if (victim.timeOutsideBubble <= victim.DESPAWN_SAFETY_WINDOW) {
				continue;
			}
			// First false is to not drop items, second is to not drop particles
			victim.prepareToDie(false, false);
			this.things.splice(i, 1);
			this.thingDisplays.removeChild(victim.display);
		}

		// Calculate a weight for each spawn pool
		let currentWeights = new Map(Object.values(Density.POOLS).map(pool => [pool, 0]));
		for (let thing of this.things) {
			// Cheap check first
			if (thing.GUESSTIMATION_WEIGHT === 0) continue;
			if (this._guesstimator.isOutsideBubble(thing.pos)) continue;
			console.assert(thing.pool !== null);
			currentWeights.set(
				thing.pool,
				currentWeights.get(thing.pool) + thing.GUESSTIMATION_WEIGHT
			);
		}

		let targetWeights = this.density.getDensities(this.player.pos, this.time);
		targetWeights.forEach((v, k, map) => {
			map.set(k, v * this._guesstimator.getOversize())
		});

		// Spawn new things
		targetWeights.forEach((targetWeight, pool) => {
			while (currentWeights.get(pool) < targetWeight) {
				let freshSpawn = this.requestSpawn(
					...this.density.selectThing(
						this._guesstimator.selectSpawn(),
						this.time,
						pool
					)
				);
				if (freshSpawn.GUESSTIMATION_WEIGHT <= 0) {
					logger.error(
						"space.js",
						"Attempting to spawn things with GUESSTIMATION_WEIGHT <= 0"
										+ " can crash the game, aborting spawning"
										+ " for the tick"
					);
					return;
				}
				currentWeights.set(
					pool,
					currentWeights.get(pool) + freshSpawn.GUESSTIMATION_WEIGHT
				);
			}
		});
	}

	// Generate a new tactical report
	_generateTacticalReport() {
		this._tacticalReportFull = this.things
			.filter(thing => (thing.state === Thing.STATES.ALIVE))
			.map(thing => {
				return {
					// Commonly needed properties
					id:                thing.id,
					TYPE:              thing.TYPE,
					CAN_PICK_UP_ITEMS: thing.CAN_PICK_UP_ITEMS,
					IS_BOSS:           (thing instanceof Meteorus),
					name:              thing.name,
					pos:               new Vector(thing.pos),
					// Reference to the thing - can be checked for more obscure properties
					thing:             thing
				}
			});
		this._tacticalReportCache = new Map();
	}

	// Move all things
	// dt is the time since last update, in seconds.
	_move(dt) {
		for (let thing of this.things) {
			thing.move(dt, this.time);
		}
	}

	// Collide all things with each other
	// dt is the time since last update, in seconds.
	_collide(dt) {
		// Assume we can't dock, interactor will correct this if necessary
		this.canDock = false;

		this.interactor.handleAllInteractions(dt, this.time);

		// If the player is already docking, then we really can't dock, whatever
		// the interactor says
		if (this.player.dockingTo !== null) this.canDock = false;
	}

	// Destroy all dead things
	_kill() {
		for (let i = this.things.length; i--;) {
			let thing = this.things[i];
			if (thing.state === Thing.STATES.DEAD) {
				this.things.splice(i, 1);
				this.thingDisplays.removeChild(thing.display);
			}
		}
	}

	// Redraw everything on the screen
	// screenSize is a vector containing the screen size, in pixels.
	// pixelsPerFlare is a real number.
	_redraw(screenSize, pixelsPerFlare) {
		// Redraw the player first
		// By redrawing the player first, we put an accurate value into
		// requestPosToPosition().
		this.player.redraw(screenSize, pixelsPerFlare);
		for (let thing of this.things) {
			if (thing !== this.player) thing.redraw(screenSize, pixelsPerFlare);
		}
		this._grid.redraw(
			screenSize,
			pixelsPerFlare,
			this.requestPosToPosition(new Vector(), pixelsPerFlare)
		);
		this.scanner.redraw(screenSize, pixelsPerFlare);
	}

	// Start firing the weapon
	_startFiring() {
		this.player.firingMouse = true;
	}

	// Stop firing the weapon
	_stopFiring() {
		this.player.firingMouse = false;
	}
}
